"use client";

import React from "react";
import {
  Dot,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface DataItem {
  subject: string;
  A: number;
  fullMark: number;
}

interface RadarChartsProps {
  data: DataItem[];
  candidateName?: string;
  colorScheme?: {
    average: string;
    candidate: string;
  };
}

// Custom tooltip for better data visualization
const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const subject = label;
    const average = payload[0]?.value;
    const candidate = payload[1]?.value;
    const difference = candidate - average;
    const diffText = difference >= 0 ? `+${difference}` : `${difference}`;
    const diffColor = difference >= 0 ? "text-green-600" : "text-red-600";

    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-neutral-200">
        <h3 className="font-medium mb-1">{subject}</h3>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          <div className="text-neutral-500">Average:</div>
          <div className="font-medium">{average}%</div>
          <div className="text-neutral-500">Candidate:</div>
          <div className="font-medium">{candidate}%</div>
          <div className="text-neutral-500">Difference:</div>
          <div className={`font-medium ${diffColor}`}>{diffText}%</div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom render for radar dots with value labels
// eslint-disable-next-line react/display-name
const renderCustomDot = (color: string) => (props: any) => {
  const { cx, cy, payload, dataKey } = props;
  const value = payload[dataKey];

  // Calculate text position - ensure it's within bounds
  let textDy = -10;
  if (cy < 30) textDy = 20; // If too close to top, move below

  return (
    <g>
      <Dot cx={cx} cy={cy} r={4} fill={color} />
      <text
        x={cx}
        y={cy}
        dy={textDy}
        fontSize={11}
        fontWeight="600"
        textAnchor="middle"
        fill={color}
      >
        {value}
      </text>
    </g>
  );
};

// Custom legend for better visual representation
const CustomLegend: React.FC<any> = (props) => {
  const { payload, candidateName } = props;

  return (
    <div className="flex justify-center items-center gap-8 mt-2">
      {payload.map((entry: any, index: number) => (
        <div key={`legend-${index}`} className="flex items-center">
          <div
            className="w-4 h-4 rounded-sm mr-2"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-sm font-medium">
            {entry.value === "Average"
              ? "Average"
              : candidateName || "Candidate"}
          </span>
        </div>
      ))}
    </div>
  );
};

const RadarCharts: React.FC<RadarChartsProps> = ({
  data,
  candidateName,
  colorScheme = {
    average: "#4338ca", // Indigo for average
    candidate: "#15a349", // Green for candidate
  },
}) => {
  return (
    <div className="radar-chart-container">
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart
          cx="50%"
          cy="50%"
          outerRadius="70%"
          data={data}
          margin={{ top: 10, right: 30, bottom: 10, left: 30 }}
        >
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="subject"
            tick={{
              fontSize: 12,
              fontWeight: 500,
              fill: "#4b5563", // Better text contrast
            }}
            tickLine={false}
            stroke="#9ca3af"
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            stroke="#9ca3af"
            tickCount={5}
            tick={{ fontSize: 10 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Radar
            name="Average"
            dataKey="A"
            stroke={colorScheme.average}
            fill={colorScheme.average}
            strokeWidth={2}
            fillOpacity={0.2}
            dot={renderCustomDot(colorScheme.average)}
            animationBegin={200}
            animationDuration={800}
          />
          <Radar
            name="Candidate"
            dataKey="B"
            stroke={colorScheme.candidate}
            fill={colorScheme.candidate}
            strokeWidth={2}
            fillOpacity={0.3}
            dot={renderCustomDot(colorScheme.candidate)}
            animationBegin={400}
            animationDuration={800}
          />
          <Legend
            content={<CustomLegend candidateName={candidateName} />}
            verticalAlign="bottom"
            height={36}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RadarCharts;
